// Accessible markup for the capture surface, shared by the SPA and the a11y test
// (REQ-NFR-A11Y-001). Pure string builders — no DOM, no Supabase. The See surface lives in
// ./render-see.ts. escapeHtml is re-exported here for existing importers.
import type { Extraction, MultiDraftModel } from "./extract";
import type { ViewItem } from "./view";
import type { IngestStats, MailboxState } from "./ingest-stats";
import { relativeTime } from "./ingest-stats";
import type { FeedbackItem } from "./feedback";
import { FEEDBACK_CATEGORIES } from "./feedback";
import { icon, type IconName } from "./icons";
import { initials } from "./owner";
import { THEME_PRESETS } from "./member-theme";
import { ownerColorIndex } from "./planner";
import type { ConfirmationModel } from "./planner";
import { recurrenceLabel, REPEAT_PRESETS, presetForRrule } from "./recurrence";

/** The shared bottom-sheet chrome (mockup-v9): a grab handle, a header (title + x close), then the
 *  caller's body + footer. Every sheet uses this so they are structurally identical to the mock. */
export function sheetShell(titleId: string, title: string, closeAct: string, inner: string): string {
  return `<div class="sheet-backdrop" data-act="${closeAct}"></div>
    <section class="sheet" role="dialog" aria-modal="true" aria-labelledby="${titleId}" tabindex="-1">
      <div class="grab" aria-hidden="true"></div>
      <div class="shh"><h3 id="${titleId}">${escapeHtml(title)}</h3><button type="button" class="tool-btn sheet-x" data-act="${closeAct}" aria-label="Close">${icon("cancel")}</button></div>
      ${inner}
    </section>`;
}
const checkRow = (name: string, label: string, checked = false): string =>
  `<label class="checkrow"><input type="checkbox" name="${name}"${checked ? " checked" : ""} /><span class="box" aria-hidden="true">${icon("check", 16)}</span> ${escapeHtml(label)}</label>`;

export interface ItemSheetMember { id: string; name: string; color?: string | null }
export interface ItemSheetOpts { members?: ItemSheetMember[]; ownerIds?: string[]; timeValue?: string; endTimeValue?: string }
/** Split a grocery title into an optional leading quantity + name ("2x Milk" → {qty:"2", name:"Milk"}). */
export function splitGroceryTitle(title: string): { qty: string; name: string } {
  const m = title.match(/^(\d+)\s*[x×]\s+(.*)$/i);
  return m ? { qty: m[1], name: m[2] } : { qty: "", name: title };
}
/** Rejoin a grocery name + quantity into a storable title. */
export function joinGroceryTitle(name: string, qty: string): string {
  const q = qty.trim();
  return q ? `${q}x ${name.trim()}` : name.trim();
}

// Event duration presets (REQ-UX-007↺): pick a length instead of a fiddly end-time picker, so When
// stays one row. "" = no set end. A custom option covers an existing end that isn't a round preset.
const DUR_PRESETS: Array<[string, string]> = [["", "–"], ["15", "15m"], ["30", "30m"], ["60", "1h"], ["120", "2h"], ["240", "4h"]];
function fmtDur(min: number): string {
  const h = Math.floor(min / 60), m = min % 60;
  return h && m ? `${h}h ${m}m` : h ? `${h}h` : `${m}m`;
}
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
/** "2026-08-18" → "18 Aug '26" — a format that's hard to misread (vs an ambiguous dd/mm vs mm/dd).
 *  Used as the visible label over the native date input (REQ-UX-007↺). Empty → "". */
export function formatDateShort(yyyyMmDd?: string): string {
  const m = (yyyyMmDd ?? "").match(/^(\d{4})-(\d{2})-(\d{2})$/);
  return m ? `${+m[3]} ${MONTHS[+m[2] - 1]} '${m[1].slice(2)}` : "";
}

/** Duration <option>s for the Edit When row: presets + a "custom" entry for any existing end that
 *  doesn't line up with a preset. Reads the start/end HH:MM the sheet was opened with. */
export function durationOptions(timeValue?: string, endTimeValue?: string): string {
  const toMin = (t?: string) => { const m = (t ?? "").match(/^(\d{2}):(\d{2})$/); return m ? +m[1] * 60 + +m[2] : null; };
  const s = toMin(timeValue), e = toMin(endTimeValue);
  const cur = s != null && e != null && e > s ? String(e - s) : "";
  const known = DUR_PRESETS.some(([v]) => v === cur);
  return DUR_PRESETS.map(([v, l]) => `<option value="${v}"${v === cur ? " selected" : ""}>${l}</option>`).join("") +
    (!known && cur ? `<option value="${cur}" selected>${fmtDur(+cur)}</option>` : "");
}

const DRAFT_TYPE_WORD: Record<string, string> = { task: "Task", event: "Event", note: "Note", grocery: "Grocery" };

/** The multi-item review list (REQ-CAPTURE-010) — one input → several proposed drafts, multi-select
 *  to keep (dupes pre-unselected + flagged, context spans labelled), edit/discard → one commit. */
export interface ReviewOpts { provenance?: string[]; headNote?: string }
export function reviewListHTML(m: MultiDraftModel, parents: string[] = [], opts: ReviewOpts = {}): string {
  const rows = m.drafts
    .map((d, i) => {
      const draft = d.extraction.draft;
      const bits = [DRAFT_TYPE_WORD[draft.type] ?? "Item"];
      if (draft.due_at) bits.push(draft.due_at.slice(0, 10));
      if (draft.owners.length) bits.push(draft.owners.join(", "));
      if (d.kind === "context") bits.push("context");
      if (d.duplicate) bits.push("already have this");
      // Provenance (REQ-INGEST-004) — where an ingested draft came from, so the parse is trustable.
      const prov = opts.provenance?.[i] ? `<span class="review-prov">${escapeHtml(opts.provenance[i])}</span>` : "";
      const row = `<label class="review-row${d.duplicate ? " dup" : ""}">
        <input type="checkbox" name="keep" value="${i}"${d.selected ? " checked" : ""} />
        <span class="review-body">
          <span class="review-title">${escapeHtml(draft.title || "(untitled)")}</span>
          <span class="review-meta">${escapeHtml(bits.join(" · "))}</span>
          ${prov}
        </span>
      </label>`;
      // Conversational clarify (REQ-CAPTURE-014): a child's event asks who's taking them; pick an
      // adult → they become a co-owner alongside the child.
      const takes = d.needsAdult
        ? `<div class="who-takes"><span class="who-q">Who's taking ${escapeHtml(draft.owners.join(" & ") || "them")}?</span>${parents
            .map((p) => `<button type="button" class="who-chip" data-adult-opt="${i}::${escapeHtml(p)}">${escapeHtml(p)}</button>`)
            .join("")}</div>`
        : "";
      return row + takes;
    })
    .join("");
  return `<form class="review" aria-label="Review these items">
    <div class="shbody">
      <p class="review-head">${m.drafts.length} found — keep the ones you want. Any that clash with your calendar will update it, not duplicate.${opts.headNote ? ` <span class="review-note">${escapeHtml(opts.headNote)}</span>` : ""}</p>
      ${rows}
    </div>
    <div class="sheetfoot">
      <button type="button" class="btn" data-act="capture-close">Cancel</button>
      <button type="submit" class="btn primary">Add selected</button>
    </div>
  </form>`;
}

/** The inferred-draft confirmation (REQ-UX-005) — a compact "Added: …" restatement with type/date/
 *  owner/destination chips and an Edit escape, announced via role=status (the caller auto-dismisses).
 *  The full form (draftHTML) appears only on a blocking-field failure or explicit Edit. */
export function confirmationHTML(m: ConfirmationModel): string {
  const chips = m.chips.map((c) => `<span class="cap-chip cap-${c.kind}">${escapeHtml(c.text)}</span>`).join("");
  return `<div class="cap-confirm" role="status" aria-live="polite">
    <span class="cap-added"><span class="cap-tick" aria-hidden="true">✓</span> Added: <b>${escapeHtml(m.title)}</b></span>
    <span class="cap-chips">${chips}</span>
    <button type="button" class="cap-edit" data-act="capture-edit">Edit</button>
  </div>`;
}

/** The avatar-colour palette (REQ-THEME-008) — swatches a member picks from; colours already taken
 *  by other members render as disabled marks showing their initial, so the family stays distinct. */
export function avatarPaletteHTML(
  palette: string[],
  selected: string | null | undefined,
  taken: Map<string, string>
): string {
  const sel = selected?.toLowerCase();
  return palette
    .map((c) => {
      const takenBy = taken.get(c.toLowerCase());
      if (takenBy) {
        return `<span class="swatch taken" style="background:${c}" role="img" aria-label="Taken by ${escapeHtml(takenBy)}">${escapeHtml(takenBy)}</span>`;
      }
      const mine = sel === c.toLowerCase();
      return `<button type="button" class="swatch avatar-swatch${mine ? " on" : ""}" data-avatar-color="${c}" style="background:${c}" aria-pressed="${mine}" aria-label="Use ${c} as your colour">${mine ? "✓" : ""}</button>`;
    })
    .join("");
}
export { escapeHtml } from "./html";
import { escapeHtml } from "./html";

/** The capture composer: labelled textarea + a labelled route choice + submit. Route defaults to
 *  Family; the Private/Health route is available to everyone (REQ-CAPTURE-009 — Private is always
 *  the safe direction). No capture content leaves the device for a cloud model unless Family is
 *  the active route (enforced in the capture flow / extract gate). */
/** The capture sheet (mockup-v9 #add): a bottom sheet with a single textarea carrying inline mic
 *  (soon) + camera buttons, a "Make this private" checkrow, and a plain-language helper. The
 *  Family/Health route the mock doesn't show survives as a "Health — off the cloud" checkrow
 *  (REQ-CAPTURE-009 boundary; checked → private_health, else cloud). */
export function captureSheetHTML(): string {
  return sheetShell(
    "sheet-title",
    "Add",
    "capture-close",
    `<form class="capture" aria-label="Capture something">
      <div class="shbody">
        <div class="field">
          <label for="capture-text" class="visually-hidden">What's happening?</label>
          <textarea id="capture-text" name="text" class="input" rows="2" placeholder="dentist for Amelia next Tue 3pm"></textarea>
          <div class="inbtns">
            <button type="button" class="soon" data-act="mic" title="Voice input" aria-label="Dictate with your voice">${icon("microphone")}</button>
            <button type="button" data-act="camera" title="Photo or screenshot" aria-label="Add a photo or screenshot" aria-haspopup="true">${icon("camera")}</button>
          </div>
        </div>
        <input id="capture-photo" name="photo" type="file" accept="image/*" class="visually-hidden" aria-label="Photo or screenshot" />
        <div class="cam-menu" data-cam-menu hidden>
          <button type="button" class="btn" data-act="cam-take">${icon("camera")} Take photo</button>
          <button type="button" class="btn" data-act="cam-pick">Choose photo</button>
        </div>
        ${checkRow("private", "Make this private (only me)")}
        <p class="sheet-help">Type it, or add a photo or screenshot — we'll work out if it's an event, task, grocery or note, and show you before saving.</p>
        <p class="error" role="alert" aria-live="polite"></p>
      </div>
      <div class="sheetfoot"><button type="submit" class="btn primary">Add</button></div>
    </form>`
  );
}

/** The Slipped sheet (REQ-VIEW-021) — opened from the Today "N slipped" line: the overdue items,
 *  each clearable with its tick, plus a "Dismiss all". Pure markup; the glue wires the actions. */
export function slippedSheetHTML(overdue: Array<{ id: string; title: string; sub?: string }>): string {
  const rows =
    overdue
      .map(
        (it) => `<li class="item">
        <button type="button" class="row-circle" data-act="slip-done" data-id="${escapeHtml(it.id)}" aria-label="Clear ${escapeHtml(it.title)}"><span class="circle" aria-hidden="true"></span></button>
        <button type="button" class="row-open" data-act="slip-open" data-id="${escapeHtml(it.id)}"><span class="it-body"><span class="it-title">${escapeHtml(it.title)}</span>${it.sub ? `<span class="it-sub">${escapeHtml(it.sub)}</span>` : ""}</span></button>
      </li>`
      )
      .join("") || `<p class="muted" style="padding:12px">Nothing overdue — you're on top of it.</p>`;
  const foot = overdue.length
    ? `<div class="sheetfoot"><button type="button" class="btn link" data-act="slip-dismiss-all" style="margin-right:auto">Dismiss all</button><button type="button" class="btn" data-act="capture-close">Close</button></div>`
    : `<div class="sheetfoot"><button type="button" class="btn" data-act="capture-close">Close</button></div>`;
  return sheetShell("slip-h", overdue.length ? `${overdue.length} slipped` : "Nothing slipped", "capture-close",
    `<div class="shbody"><ul class="panel items" style="margin-bottom:12px">${rows}</ul></div>${foot}`);
}

/** The polite confirmation string (REQ-UX-002). */
export function announcement(title: string, day?: string): string {
  return day ? `Added — ${title}, ${day} ✓` : `Added — ${title} ✓`;
}

/** "Make it mine" — the personalization sheet (feature 004.1): curated Themes (REQ-THEME-001↺) +
 *  a member avatar colour (REQ-THEME-008) + a chrome-only background (REQ-THEME-003↺). The free
 *  accent picker and the font picker are retired (the type scale is fixed by the design system). */
export function settingsSheetHTML(selectedTheme?: string | null, hasBackground = false): string {
  const sel = THEME_PRESETS.some((p) => p.name === selectedTheme) ? selectedTheme : "calm";
  const cards = THEME_PRESETS.map(
    (p) =>
      `<button type="button" class="tcard${p.name === sel ? " on" : ""}" data-theme-preset="${p.name}" aria-pressed="${p.name === sel}">
        <b class="theme-name">${escapeHtml(p.label)}</b>
        <span class="tswatch theme-swatch" style="background:${p.base}"></span>
      </button>`
  ).join("");
  return sheetShell("settings-title", "Make it mine", "settings-close",
    `<form class="settings" aria-label="Personalize the app">
      <div class="shbody">
        ${hasBackground
          ? `<p class="sheet-help" style="margin-top:0">Your theme colour comes from your background photo. Remove the photo to choose a Theme instead.</p>`
          : `<label class="f">Theme</label><div class="mim-theme">${cards}</div>`}
        <label class="f" id="avatar-colour-label">My avatar colour <span class="muted" style="font-weight:400">(taken colours are marked)</span></label>
        <div class="palette" data-avatar-palette role="group" aria-labelledby="avatar-colour-label"></div>
        <span class="f" id="bg-label">Background photo <span class="muted" style="font-weight:400">(a washed-out backdrop behind your content)</span></span>
        <div class="bg-controls">
          <label class="btn" for="bg-input">Choose a photo</label>
          <input class="visually-hidden" id="bg-input" name="background" type="file" accept="image/*" aria-labelledby="bg-label" />
          ${hasBackground ? `<button type="button" class="btn" data-act="bg-remove">Remove photo</button>` : ""}
        </div>
        <div class="swatch-row" data-swatches></div>
        <p class="sheet-help">All colours here are checked to work with white text (WCAG AA). Your photo sits behind the app; your lists always stay on a clear surface.</p>
      </div>
      <div class="sheetfoot"><button type="submit" class="btn primary">Done</button></div>
    </form>`);
}

/** The Account sheet (feature 011): the mailbox ingestion stats (REQ-STATS-001) + the family
 *  feedback board (REQ-FEEDBACK-001/002). Pure over the two models; the glue wires the submit. */
function statTile(n: number, label: string, tone: string): string {
  return `<div class="acct-stat ${tone}"><b>${n}</b><span>${escapeHtml(label)}</span></div>`;
}
function shortDate(iso: string): string {
  try {
    return new Intl.DateTimeFormat("en-NZ", { timeZone: "Pacific/Auckland", day: "numeric", month: "short" }).format(new Date(iso));
  } catch {
    return "";
  }
}
export function accountSheetHTML(stats: IngestStats, mailbox: MailboxState, feedback: FeedbackItem[], opts: { nowISO: string; canSeeDiverted?: boolean }): string {
  const checked = relativeTime(stats.lastCheck, opts.nowISO);
  const scans = stats.runsToday === 1 ? "1 scan" : `${stats.runsToday} scans`;
  // The STANDING state (REQ-STATS-002) — real, immediate, from the live rows (not per-run telemetry).
  const grid = `<div class="acct-stat-grid">
      ${statTile(mailbox.toReview, "To review", "ok")}
      ${statTile(mailbox.headsUp, "Heads-up", "health")}
      ${statTile(mailbox.filed, "Filed", "muted")}
      ${statTile(mailbox.ignored, "Ignored", "muted")}
    </div>`;
  const at = stats.allTime;
  const divertedLine = opts.canSeeDiverted
    ? `<p class="acct-alltime">${mailbox.diverted} kept private (health) · this week ${at.total} processed</p>`
    : `<p class="acct-alltime">this week ${at.total} processed</p>`;
  const mailboxSec = `<section class="acct-sec" aria-labelledby="acct-mail-h">
      <h4 id="acct-mail-h" class="acct-h">Mailbox</h4>
      <p class="acct-meta">Last checked <b>${escapeHtml(checked)}</b>${stats.runsToday ? ` · ${scans} today` : ""}</p>
      ${grid}
      ${divertedLine}
      <p class="acct-drive">${icon("note", 16)} Drive attachments — <span class="muted">${stats.driveConnected ? "connected — watching your folder" : "not connected yet"}</span></p>
    </section>`;

  const list = feedback.length
    ? `<ul class="fb-list">${feedback
        .map(
          (f) => `<li class="fb-item">
          <div class="fb-top"><span class="fb-badge ${f.statusTone}">${escapeHtml(f.statusLabel)}</span><span class="fb-cat cat-${f.category}">${escapeHtml(f.categoryLabel)}</span>${f.author ? `<span class="fb-who">${escapeHtml(f.author)}</span>` : ""}<span class="fb-date">${escapeHtml(shortDate(f.createdAt))}</span></div>
          <p class="fb-body">${escapeHtml(f.body)}</p>
        </li>`
        )
        .join("")}</ul>`
    : `<p class="fb-empty muted">No ideas yet — be the first.</p>`;
  // "What is this?" segment (mirrors MyTrolley) — sets the reader's expectation. Default = Just saying.
  const seg = FEEDBACK_CATEGORIES.map(
    (c, i) => `<button type="button" class="fb-seg-btn" role="radio" aria-checked="${i === FEEDBACK_CATEGORIES.length - 1}" data-cat="${c.value}" data-placeholder="${escapeHtml(c.placeholder)}">${icon(c.icon as IconName, 16)}<span>${escapeHtml(c.label)}</span></button>`
  ).join("");
  const defaultPlaceholder = FEEDBACK_CATEGORIES[FEEDBACK_CATEGORIES.length - 1].placeholder;
  const feedbackSec = `<section class="acct-sec" aria-labelledby="acct-fb-h">
      <h4 id="acct-fb-h" class="acct-h">Ideas &amp; feedback</h4>
      <form class="fb-form" aria-label="Share an idea">
        <span class="f fb-seg-label" id="fb-seg-label">What is this?</span>
        <div class="seg fb-seg" role="radiogroup" aria-labelledby="fb-seg-label">${seg}</div>
        <input type="hidden" name="category" value="other" />
        <label class="visually-hidden" for="fb-idea">Your idea</label>
        <textarea class="input" id="fb-idea" name="idea" rows="3" placeholder="${escapeHtml(defaultPlaceholder)}" maxlength="2000"></textarea>
        <div class="fb-form-foot"><span class="fb-err" role="alert" aria-live="polite"></span><button type="submit" class="btn primary fb-send" data-act="fb-send">${icon("navigation", 16)} Send</button></div>
      </form>
      ${list}
    </section>`;

  return sheetShell("account-title", "Account", "account-close",
    `<div class="shbody acct">${mailboxSec}${feedbackSec}</div>`);
}

/** The opened-item sheet (REQ-VIEW-014) — tapping a row opens it here, where its actions live
 *  (component-a11y-spec §4): reschedule (date/time), mark done, Private toggle (REQ-ITEM-005),
 *  and Delete behind a confirm. External read-only rows open a detail view with no edit controls.
 *  `dateValue` is the item's local date (tz-aware), supplied by the caller; rescheduling moves the
 *  day and keeps the original time-of-day (full time editing is a later brief). */
const ITEM_TYPES: Array<[string, string]> = [["task", "Task"], ["event", "Event"], ["note", "Note"], ["grocery", "Grocery"]];
// The mock footer: Delete (link, left) · Cancel · Save (right). Delete is 2-step (armed by the glue),
// keeping the single-tap-safe delete gate (REQ-VIEW-014) without cluttering the footer.
const editFoot = `<div class="sheetfoot" data-editfoot><button type="button" class="btn link" data-act="delete">Delete</button><button type="button" class="btn" data-act="item-close">Cancel</button><button type="submit" class="btn primary">Save</button></div>`;

export function itemSheetHTML(it: ViewItem, dateValue = "", opts: ItemSheetOpts = {}): string {
  const t = escapeHtml(it.title ?? "");
  const priv = it.visibility === "private";
  const readonly = typeof it.source === "string" && it.source.startsWith("external:");
  const members = opts.members ?? [];
  const ownerIds = opts.ownerIds ?? [];

  if (readonly) {
    return sheetShell("item-title-h", "Details", "item-close",
      `<div class="shbody"><p class="muted">This came from an external calendar and is read-only here.</p></div>
       <div class="sheetfoot"><button type="button" class="btn" data-act="item-close">Close</button></div>`);
  }

  // Grocery → Item + Quantity (mockup-v9 #gedit); quantity round-trips through the title.
  if (it.type === "grocery") {
    const { qty, name } = splitGroceryTitle(it.title ?? "");
    return sheetShell("item-title-h", "Edit item", "item-close",
      `<form class="item-edit" aria-label="Edit grocery">
        <div class="shbody">
          <label class="f" for="gro-name">Item</label>
          <input class="input" id="gro-name" name="gname" type="text" value="${escapeHtml(name)}" />
          <label class="f" for="gro-qty">Quantity <span class="muted" style="font-weight:400">(optional)</span></label>
          <input class="input" id="gro-qty" name="gqty" type="text" value="${escapeHtml(qty)}" placeholder="e.g. 2" />
        </div>
        ${editFoot}
      </form>`);
  }

  // Task / Event / Note — Title + Type + avatars-only owners + When, with Location/Repeat/Details
  // revealed on demand (REQ-UX-007) so the sheet opens light and only shows what's in use.
  const typeSeg = ITEM_TYPES.map(([v, l]) => `<button type="button" class="seg-btn${it.type === v ? " on" : ""}" data-type-opt="${v}" aria-pressed="${it.type === v}">${l}</button>`).join("");
  const ownerPick = members
    .map((m) => {
      const on = ownerIds.includes(m.id);
      // Distinct colour per member (mockup-v9): the chosen member_color, else the deterministic
      // owner-N so two people are never the same colour. Avatar only — the name is the aria-label /
      // tooltip; unselected avatars render greyed (CSS), selected fill with the member's colour.
      const idx = ownerColorIndex(m.id);
      const cls = m.color ? "owner-av" : `owner-av owner-${idx}`;
      const st = m.color ? ` style="background:${escapeHtml(m.color)}"` : "";
      return `<button type="button" class="own-btn${on ? " on" : ""}" data-owner-opt="${escapeHtml(m.id)}" aria-pressed="${on}" aria-label="${escapeHtml(m.name)}" title="${escapeHtml(m.name)}"><span class="${cls}" aria-hidden="true"${st}>${escapeHtml(initials(m.name))}</span></button>`;
    })
    .join("");
  const hasLoc = !!(it.location as string);
  const hasRepeat = !!(it.rrule as string);
  const hasDetails = !!(it.details as string);
  const addBtn = (field: string, ic: IconName, label: string) =>
    `<button type="button" class="btn ghost small add-field-btn" data-reveal="${field}">${icon(ic, 16)}<span>${label}</span></button>`;
  return sheetShell("item-title-h", "Edit", "item-close",
    `<form class="item-edit" aria-label="Edit item">
      <div class="shbody">
        <label class="f" for="item-title">Title</label>
        <div class="field title-field">
          <input class="input" id="item-title" name="title" type="text" value="${t}" />
          <button type="button" class="refine-icon" data-act="refine-toggle" aria-expanded="false" aria-label="Refine with AI" title="Refine with AI">${icon("sparkle", 18)}</button>
        </div>
        <div class="refine-panel" data-refine-panel hidden>
          <input class="input" name="refine" placeholder="What should change? e.g. move to Fri 3pm, add Amelia, make it weekly" aria-label="Describe the change" />
          <div class="refine-row">
            <button type="button" class="btn primary" data-act="refine-run">${icon("sparkle", 16)}<span>Refine with AI</span></button>
            <p class="refine-status" data-refine-status role="status" aria-live="polite"></p>
          </div>
        </div>
        <div data-lower>
          <label class="f">Type</label>
          <div class="seg" role="group" aria-label="Type" data-type-pick>${typeSeg}</div>
          <p class="sheet-help" data-note-help${it.type === "note" ? "" : " hidden"}>${icon("note", 14)} A note is a quick reference jotting — it has no date and lives in your Tasks list, not the calendar.</p>
          ${members.length ? `<label class="f">Who?</label>
          <div class="own" role="group" aria-label="Who is it for" data-owner-pick>${ownerPick}</div>` : ""}
          <label class="f">When</label>
          <div class="item-when">
            <span class="when-col when-col--date">
              <span class="when-cap">Date</span>
              <span class="date-proxy">
                <input class="input when-date" id="item-date" name="date" type="date" value="${escapeHtml(dateValue)}" aria-label="Date" />
                <span class="date-display" data-date-display aria-hidden="true">${escapeHtml(formatDateShort(dateValue))}</span>
              </span>
            </span>
            <span class="when-col">
              <span class="when-cap">Start</span>
              <input class="input when-time" id="item-time" name="time" type="time" value="${escapeHtml(opts.timeValue ?? "")}" aria-label="Start" />
            </span>
            <span class="when-col">
              <span class="when-cap">Duration</span>
              <select class="input when-dur" id="item-dur" name="duration" aria-label="Duration">${durationOptions(opts.timeValue, opts.endTimeValue)}</select>
            </span>
          </div>
          <div class="add-more" data-add-more>
            ${!hasLoc ? addBtn("location", "map-pin", "Location") : ""}
            ${!hasRepeat ? addBtn("repeat", "repeat", "Repeat") : ""}
            ${!hasDetails ? addBtn("details", "note", "Details") : ""}
          </div>
          <div class="field-block" data-field="location"${hasLoc ? "" : " hidden"}>
            <label class="f" for="item-location">Location <span class="muted" style="font-weight:400">(optional)</span></label>
            <input class="input" id="item-location" name="location" type="text" value="${escapeHtml((it.location as string) ?? "")}" placeholder="e.g. Highbrook Medical" />
            <div class="loc-actions">
              <button type="button" class="btn ghost small" data-act="find-on-map">${icon("map-pin", 16)}<span>Find on map</span></button>
              <button type="button" class="btn ghost small" data-act="navigate">${icon("navigation", 16)}<span>Directions</span></button>
            </div>
          </div>
          <div class="field-block" data-field="repeat"${hasRepeat ? "" : " hidden"}>
            <label class="f" for="item-repeat">Repeat</label>
            <select class="input" id="item-repeat" name="repeat" aria-describedby="repeat-note">${((): string => {
              const cur = presetForRrule(it.rrule as string | null);
              const opts = REPEAT_PRESETS.map((p) => `<option value="${p.value}"${p.value === cur ? " selected" : ""}>${escapeHtml(p.label)}</option>`).join("");
              const custom = cur === "custom" ? `<option value="custom" selected>${escapeHtml(recurrenceLabel(it.rrule as string))} (custom)</option>` : "";
              return opts + custom;
            })()}</select>
            <input type="hidden" name="rrule" value="${escapeHtml((it.rrule as string) ?? "")}" />
            <p class="sheet-help" id="repeat-note" data-recur${it.rrule ? "" : " hidden"}>${it.rrule ? `${icon("repeat", 14)} Repeats ${escapeHtml(recurrenceLabel(it.rrule as string))}` : ""}</p>
          </div>
          <div class="field-block" data-field="details"${hasDetails ? "" : " hidden"}>
            <label class="f" for="item-details">Details <span class="muted" style="font-weight:400">(optional)</span></label>
            <textarea class="input details-input" id="item-details" name="details" rows="3" placeholder="A note or short list — a couple of sentences, or a few points">${escapeHtml((it.details as string) ?? "")}</textarea>
          </div>
          ${checkRow("private", "Make this private (only me)", priv)}
        </div>
      </div>
      ${editFoot}
    </form>`);
}

/** The draft review + confirm step (Principle 7 — always shown before commit). The optional
 *  restatement reads the draft back as a plain sentence (REQ-UX-001). */
/** The review-before-add drawer (REQ-CAPTURE-004/015). EDITABLE — a photo's OCR is often wrong, so
 *  every field can be corrected here before it commits. `dateValue`/`timeValue` prefill the When from
 *  the draft's due (local, tz-resolved by the caller); the glue reads the fields back on Add. */
export function draftHTML(e: Extraction, restatement?: string, opts: { dateValue?: string; timeValue?: string } = {}): string {
  const d = e.draft;
  const typeOpt = (v: string, label: string) => `<option value="${v}"${d.type === v ? " selected" : ""}>${label}</option>`;
  return `
    <form class="draft draft-edit" aria-label="Check and add this item">
      ${restatement ? `<p class="restatement">${escapeHtml(restatement)}</p>` : ""}
      <label class="f" for="d-title">Title</label>
      <input class="input" id="d-title" name="d-title" type="text" value="${escapeHtml(d.title ?? "")}" placeholder="What is it?" />
      <label class="f" for="d-type">Type</label>
      <select class="input" id="d-type" name="d-type">${typeOpt("task", "Task")}${typeOpt("event", "Event")}${typeOpt("note", "Note")}${typeOpt("grocery", "Grocery")}</select>
      <label class="f">When <span class="muted" style="font-weight:400">(optional for a task/note)</span></label>
      <div class="item-when">
        <input class="input" id="d-date" name="d-date" type="date" value="${escapeHtml(opts.dateValue ?? "")}" aria-label="Date" />
        <input class="input" id="d-time" name="d-time" type="time" value="${escapeHtml(opts.timeValue ?? "")}" aria-label="Time" />
      </div>
      <label class="f" for="d-loc">Location <span class="muted" style="font-weight:400">(optional)</span></label>
      <input class="input" id="d-loc" name="d-loc" type="text" value="${escapeHtml((d.location as string) ?? "")}" placeholder="e.g. Kohkoz" />
      ${checkRow("private", "Keep private (only me)")}
      <p class="draft-err" data-draft-err role="alert" aria-live="polite"></p>
      <div class="actions">
        <button type="submit" class="btn primary">Add</button>
        <button type="button" class="btn secondary" data-act="discard">Discard</button>
      </div>
    </form>`;
}

